import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Clock, MapPin, Users, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, startOfDay, addWeeks, subWeeks, eachDayOfInterval, isSameMonth, isSameDay } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EVENT_COLORS = {
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  green: "bg-green-100 text-green-700 border-green-200",
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
  teal: "bg-teal-100 text-teal-700 border-teal-200",
};

export default function MailCalendar() {
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventDialog, setEventDialog] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [currentDate]);

  const getDateRange = () => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(currentDate));
      const end = endOfWeek(endOfMonth(currentDate));
      return { start: start.toISOString(), end: end.toISOString() };
    }
    if (view === "week") {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    // day
    const start = startOfDay(currentDate);
    const end = new Date(start.getTime() + 86400000);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const res = await axios.get(`${API}/mail/calendar/events?start=${range.start}&end=${range.end}`, {
        headers: getHeaders(), withCredentials: true,
      });
      setEvents(res.data.events || []);
    } catch {}
    setLoading(false);
  };

  const handleCreateEvent = async (data) => {
    try {
      await axios.post(`${API}/mail/calendar/events`, data, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Event created");
      setEventDialog(false);
      fetchEvents();
    } catch {
      toast.error("Failed to create event");
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/mail/calendar/events/${id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Event deleted");
      fetchEvents();
    } catch {
      toast.error("Failed to delete event");
    }
  };

  const navigate = (direction) => {
    if (view === "month") setCurrentDate(direction > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    else if (view === "week") setCurrentDate(direction > 0 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    else setCurrentDate(direction > 0 ? addDays(currentDate, 1) : addDays(currentDate, -1));
  };

  const getEventsForDay = (day) => events.filter((e) => isSameDay(new Date(e.start_datetime), day));

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={(v) => setView(v)} className="w-auto">
            <TabsList className="rounded-none border border-slate-200 bg-white h-8">
              <TabsTrigger value="day" className="text-xs rounded-none px-3 data-[state=active]:bg-violet-50">Day</TabsTrigger>
              <TabsTrigger value="week" className="text-xs rounded-none px-3 data-[state=active]:bg-violet-50">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-xs rounded-none px-3 data-[state=active]:bg-violet-50">Month</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="h-7 w-7 p-0 rounded-none">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <h2 className="text-sm font-semibold text-slate-800 min-w-[150px] text-center">
              {view === "month" && format(currentDate, "MMMM yyyy")}
              {view === "week" && `Week of ${format(startOfWeek(currentDate), "MMM d")}`}
              {view === "day" && format(currentDate, "EEEE, MMMM d, yyyy")}
            </h2>
            <Button variant="outline" size="sm" onClick={() => navigate(1)} className="h-7 w-7 p-0 rounded-none">
              <ChevronRight className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())} className="h-7 text-xs px-2 rounded-none">
              Today
            </Button>
          </div>
        </div>

        <Dialog open={eventDialog} onOpenChange={setEventDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
              <Plus className="w-3 h-3 mr-1" /> New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle className="text-sm">New Event</DialogTitle>
            </DialogHeader>
            <EventForm onSubmit={handleCreateEvent} onCancel={() => setEventDialog(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto py-8" />
      ) : view === "month" ? (
        <MonthView currentDate={currentDate} events={events} getEventsForDay={getEventsForDay} onDelete={handleDeleteEvent} onDayClick={(d) => { setCurrentDate(d); setView("day"); }} />
      ) : view === "week" ? (
        <WeekView currentDate={currentDate} events={events} onDelete={handleDeleteEvent} />
      ) : (
        <DayView currentDate={currentDate} events={events} onDelete={handleDeleteEvent} />
      )}
    </div>
  );
}

function MonthView({ currentDate, events, getEventsForDay, onDelete, onDayClick }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  return (
    <div className="flex-1 bg-white border border-slate-200">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-slate-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-1.5 text-[10px] font-medium text-slate-400 text-center uppercase">{d}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 flex-1">
        {days.map((day) => {
          const dayEvents = getEventsForDay(day);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, currentDate);

          return (
            <div
              key={day.toISOString()}
              onClick={() => onDayClick(day)}
              className={`min-h-[80px] border-b border-r border-slate-100 p-1 cursor-pointer hover:bg-app transition-colors ${
                !isCurrentMonth ? "bg-app/50" : ""
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full ${
                isToday ? "bg-violet-600 text-white font-semibold" : isCurrentMonth ? "text-slate-700" : "text-slate-300"
              }`}>
                {format(day, "d")}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayEvents.slice(0, 3).map((ev) => (
                  <div key={ev.id}
                    className={`text-[9px] px-1 py-0.5 rounded truncate border ${EVENT_COLORS[ev.color] || EVENT_COLORS.blue}`}
                  >
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] text-slate-400 px-1">+{dayEvents.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ currentDate, events, onDelete }) {
  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="flex-1 bg-white border border-slate-200 overflow-auto">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {days.map((day) => (
          <div key={day.toISOString()} className="px-2 py-1.5 text-center border-r border-slate-100">
            <p className="text-[10px] font-medium text-slate-400">{format(day, "EEE")}</p>
            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded-full ${
              isSameDay(day, new Date()) ? "bg-violet-600 text-white font-semibold" : "text-slate-700"
            }`}>
              {format(day, "d")}
            </span>
          </div>
        ))}
      </div>
      <div className="p-2 space-y-1">
        {events.filter((e) => isSameDay(new Date(e.start_datetime), currentDate)).map((ev) => (
          <EventCard key={ev.id} event={ev} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function DayView({ currentDate, events, onDelete }) {
  const dayEvents = events.filter((e) => isSameDay(new Date(e.start_datetime), currentDate))
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));

  return (
    <div className="flex-1 bg-white border border-slate-200 overflow-auto p-3">
      <h3 className="text-sm font-medium text-slate-700 mb-3">
        {dayEvents.length} event{dayEvents.length !== 1 ? "s" : ""}
      </h3>
      <div className="space-y-2">
        {dayEvents.map((ev) => (
          <EventCard key={ev.id} event={ev} onDelete={onDelete} detailed />
        ))}
        {dayEvents.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-8">No events for this day</p>
        )}
      </div>
    </div>
  );
}

function EventCard({ event, onDelete, detailed }) {
  return (
    <div className={`border rounded p-2 ${EVENT_COLORS[event.color] || EVENT_COLORS.blue}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium">{event.title}</p>
          {detailed && (
            <div className="mt-1 space-y-0.5 text-[10px] opacity-75">
              {event.description && <p>{event.description}</p>}
              {event.location && <p className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{event.location}</p>}
              {event.start_datetime && (
                <p className="flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {format(new Date(event.start_datetime), "MMM d, yyyy h:mm a")}
                  {event.end_datetime && ` - ${format(new Date(event.end_datetime), "h:mm a")}`}
                </p>
              )}
              {event.attendees?.length > 0 && (
                <p className="flex items-center gap-1"><Users className="w-2.5 h-2.5" />{event.attendees.length} attendees</p>
              )}
            </div>
          )}
        </div>
        <button onClick={() => onDelete(event.id)} className="opacity-0 group-hover:opacity-100 hover:text-red-500">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function EventForm({ onSubmit, onCancel }) {
  const [data, setData] = useState({
    title: "", description: "", location: "",
    start_datetime: "", end_datetime: "", color: "blue",
    event_type: "meeting", attendees: [],
  });

  return (
    <div className="space-y-3">
      <Input placeholder="Event title" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <Textarea placeholder="Description" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })}
        className="text-xs rounded-none" rows={2} />
      <Input placeholder="Location" value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-400">Start</label>
          <Input type="datetime-local" value={data.start_datetime} onChange={(e) => setData({ ...data, start_datetime: e.target.value })}
            className="h-8 text-xs rounded-none" />
        </div>
        <div>
          <label className="text-[10px] text-slate-400">End</label>
          <Input type="datetime-local" value={data.end_datetime} onChange={(e) => setData({ ...data, end_datetime: e.target.value })}
            className="h-8 text-xs rounded-none" />
        </div>
      </div>
      <select value={data.color} onChange={(e) => setData({ ...data, color: e.target.value })}
        className="w-full h-8 text-xs border border-slate-200 rounded-none px-2">
        <option value="blue">Blue</option>
        <option value="green">Green</option>
        <option value="red">Red</option>
        <option value="amber">Amber</option>
        <option value="purple">Purple</option>
        <option value="teal">Teal</option>
      </select>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-xs rounded-none">Cancel</Button>
        <Button size="sm" onClick={() => onSubmit(data)} className="h-8 text-xs rounded-none bg-violet-600"
          disabled={!data.title.trim() || !data.start_datetime}>Create</Button>
      </div>
    </div>
  );
}
